/**
 * Hook that conditionally triggers loadFileTypeObjects — guarded by isFilterActive.
 *
 * When a search is active (isFilterActive=true) the tree is driven entirely by
 * search results. Calling loadFileTypeObjects in that state would overwrite the
 * filtered tree with emptyModelTree, breaking mobile search where the panel
 * mounts fresh after results arrive.
 */
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef } from "react";

import { Tree } from "../../../components/objectTree/types";
import { ModelTreeFilter, ModelTreeNodeData } from "../types";
import { emptyModelTree } from "../utils";

export interface UseFileTypeLoadGuardParams {
    loadFileTypeObjects: (params: {
        fileTypes: ModelTreeFilter[];
        setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>;
        abortSignal: AbortSignal;
    }) => void;
    fileTypes: ModelTreeFilter[];
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>;
    filterAbortController: MutableRefObject<AbortController>;
    isFilterActive: boolean;
}

export function useFileTypeLoadGuard({
    loadFileTypeObjects,
    fileTypes,
    setTree,
    filterAbortController,
    isFilterActive,
}: UseFileTypeLoadGuardParams) {
    // Length of the last fileTypes this effect saw. Search clear keeps an empty
    // list the whole time, so it must not reset the tree. The standalone Model
    // Tree passes [] only when every type is selected; leaving a subset for that
    // list is what still has to discard the filtered hierarchy.
    const previousFileTypeCountRef = useRef<number | null>(null);

    useEffect(() => {
        const previousCount = previousFileTypeCountRef.current;
        previousFileTypeCountRef.current = fileTypes.length;

        // When a search is active, the tree is driven by search results — skip
        // file-type loading which would overwrite the filtered tree with emptyModelTree.
        if (isFilterActive) {
            return;
        }
        if (fileTypes.length === 0 && previousCount !== null && previousCount > 0) {
            setTree(emptyModelTree);
        }
        loadFileTypeObjects({
            fileTypes,
            setTree,
            abortSignal: filterAbortController.current.signal,
        });
    }, [loadFileTypeObjects, fileTypes, filterAbortController, setTree, isFilterActive]);
}
